import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { ClientRoomPage } from './client/ClientRoomPage';
import { ClientRevisaoPage } from './client/ClientRevisaoPage';
import { PecaRevisaoPage } from './pages/PecaRevisaoPage';
import { ClientePage } from './pages/admin/ClientePage';
import { BisPage } from './pages/admin/BisPage';
import { ProdutoPage } from './pages/admin/ProdutoPage';
import { LabHomePage, LabIndexPage, LabPage } from './pages/admin/LabPage';
import { LabBoardPage } from './pages/admin/LabBoardPage';
import { ClientesPage } from './pages/admin/ClientesPage';
import { PessoasPage } from './pages/admin/PessoasPage';
import { ValorPage } from './pages/admin/ValorPage';
import { ProspeccaoPage } from './pages/admin/ProspeccaoPage';
import { PropostaPage } from './pages/admin/PropostaPage';
import { AtividadePage } from './pages/admin/AtividadePage';
import { SistemaPage } from './pages/admin/SistemaPage';
import { ConvitePage } from './pages/ConvitePage';
import { ConectarPage } from './pages/ConectarPage';
import { PerfilPage } from './pages/PerfilPage';
import { SenhaPage } from './pages/SenhaPage';
import { ParedePage } from './pages/ParedePage';
import { ConversaPage } from './pages/ConversaPage';
import { EntrevistaAbertaPage } from './pages/EntrevistaAbertaPage';
import { EntregaPage } from './pages/EntregaPage';
import { AppShell } from './layout/AppShell';
import { useAuth } from './lib/auth';
import { hasRoleAtLeast } from '@raro/domain';
import { FontePage } from './pages/FontePage';
import { HipotesePage } from './pages/HipotesePage';
import { ORaroPage } from './pages/ORaroPage';
import { NetworkPage } from './pages/NetworkPage';
import { RastroPage, RastroSetorPage } from './pages/RastroPage';
import { NetworkPersonPage } from './pages/NetworkPersonPage';
import { HojePage } from './pages/HojePage';
import { MemoriaPage } from './pages/MemoriaPage';
import { forgetNext, LoginPage } from './pages/LoginPage';
import { NovaOrganizacaoPage } from './pages/NovaOrganizacaoPage';
import { NovoProjetoPage } from './pages/NovoProjetoPage';
import { ProjetoPage } from './pages/ProjetoPage';
import { ProjetoVerPage } from './pages/ProjetoVerPage';
import { VerPage } from './pages/VerPage';
import { WallControlPage } from './pages/WallControlPage';
import { WallScreenPage } from './wall/WallScreenPage';
import { ProjetosPage } from './pages/ProjetosPage';

function RequireAuth() {
  const { me, loading } = useAuth();
  const location = useLocation();
  useEffect(() => { if (me) forgetNext(); }, [me]);
  if (loading) return <div className="rr-page rr-faint">…</div>;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

/** Pessoas do cliente nunca entram no /internal: vão direto para a sala da sua organização. */
function InternalOnly() {
  const { me } = useAuth();
  if (me && !me.internalRole) {
    const slug = me.memberships[0]?.organization.slug;
    return <Navigate to={slug ? `/client/${slug}` : '/login'} replace />;
  }
  return <Outlet />;
}

/** Páginas só de sócio (VALOR, Atividade): a API já recusa os dados; aqui a página nem abre. */
function PartnerOnly() {
  const { me } = useAuth();
  if (me && !(me.internalRole && hasRoleAtLeast(me.internalRole, 'partner'))) return <Navigate to="/" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/inicializar" element={<LoginPage mode="bootstrap" />} />
      <Route path="/wall" element={<WallScreenPage />} />
      <Route path="/convite/:token" element={<ConvitePage />} />
      <Route path="/senha" element={<SenhaPage />} />
      <Route path="/senha/:token" element={<SenhaPage />} />
      <Route path="/conversa/:token" element={<ConversaPage />} />
      <Route path="/entrevista/:token" element={<EntrevistaAbertaPage />} />
      <Route path="/entrega/:id" element={<EntregaPage />} />
      <Route element={<RequireAuth />}>
        <Route path="client/:slug" element={<ClientRoomPage />} />
        <Route path="client/:slug/revisao/:id" element={<ClientRevisaoPage />} />
        <Route path="perfil" element={<PerfilPage />} />
        <Route path="conectar" element={<ConectarPage />} />
        <Route element={<InternalOnly />}>
        <Route path="clientes/:id/bis" element={<BisPage />} />
        <Route path="clientes/:id/lab" element={<LabIndexPage />} />
        <Route path="lab/:id" element={<LabPage />} />
        <Route path="lab/:id/prancha" element={<LabBoardPage />} />
        <Route path="rastro/:sector" element={<RastroSetorPage />} />
        <Route element={<AppShell />}>
          <Route index element={<HojePage />} />
          <Route path="projetos" element={<ProjetosPage />} />
          <Route path="projetos/novo" element={<NovoProjetoPage />} />
          <Route path="projetos/:id" element={<ProjetoPage />} />
          <Route path="projetos/:id/ver" element={<ProjetoVerPage />} />
          <Route path="organizacoes/nova" element={<NovaOrganizacaoPage />} />
          <Route path="fontes/:id" element={<FontePage />} />
          <Route path="hipoteses/:id" element={<HipotesePage />} />
          <Route path="pecas/:id/revisao" element={<PecaRevisaoPage />} />
          <Route path="memoria" element={<MemoriaPage />} />
          <Route path="wall/controle" element={<WallControlPage />} />
          <Route path="lab" element={<LabHomePage />} />
          <Route path="clientes" element={<ClientesPage />} />
          <Route path="clientes/:id" element={<ClientePage />} />
          <Route path="produtos/:id" element={<ProdutoPage />} />
          <Route path="pessoas" element={<PessoasPage />} />
          <Route element={<PartnerOnly />}>
            <Route path="valor" element={<ValorPage />} />
            <Route path="valor/prospeccao" element={<ProspeccaoPage />} />
            <Route path="valor/prospeccao/:id/proposta" element={<PropostaPage />} />
            <Route path="atividade" element={<AtividadePage />} />
          </Route>
          <Route path="sistema" element={<SistemaPage />} />
          <Route path="parede" element={<ParedePage />} />
          <Route path="ver" element={<VerPage />} />
          <Route path="o-raro" element={<ORaroPage />} />
          <Route path="rastro" element={<RastroPage />} />
          <Route path="network" element={<NetworkPage />} />
          <Route path="network/:id" element={<NetworkPersonPage />} />
        </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
